import { ApiProduct } from "data/models";
import {
  AtSign,
  Building2,
  Database,
  Globe,
  Layers,
  Mail,
  Map,
  MapPin,
  Phone,
  Search,
  type LucideIcon,
} from "lucide-react";

export const API_GROUPS = [
  "Geoscape Verify",
  "Spatial",
  "Maps & Tiles",
  "Datasets",
  "Legacy",
  "Other APIs",
] as const;

export interface ApiProductOption {
  id: string;
  product: ApiProduct;
  title: string;
  description: string;
  icon: LucideIcon;
  group: (typeof API_GROUPS)[number];
  premium?: ApiProduct["premium"];
}

type ProductPresentation = Omit<ApiProductOption, "id" | "product">;

const presentations: Record<string, ProductPresentation> = {
  predictiveapi: {
    title: "Predictive API",
    description: "Address autocomplete using the legacy Predictive API.",
    icon: Search,
    group: "Legacy",
  },
  addressesapi: {
    title: "Addresses API",
    description: "Address lookup and geocoding using the legacy Addresses API.",
    icon: MapPin,
    group: "Legacy",
    premium: "partial",
  },
  batchesapi: {
    title: "Batches API",
    description: "Bulk address processing using the legacy Batches API.",
    icon: Layers,
    group: "Legacy",
    premium: "partial",
  },
  esricompatiblelocatorapi: {
    title: "Esri Compatible Locator API",
    description: "Address geocoding for Esri applications.",
    icon: MapPin,
    group: "Legacy",
  },
  landparcelsapi: {
    title: "Land Parcels API",
    description: "Cadastral boundaries, lots and parcel information.",
    icon: Map,
    group: "Spatial",
    premium: "full",
  },
  buildingsapi: {
    title: "Buildings API",
    description: "Building footprints and attributes.",
    icon: Building2,
    group: "Spatial",
    premium: "full",
  },
  administrativeboundariesapi: {
    title: "Administrative Boundaries API",
    description: "Suburb, postcode and administrative boundaries.",
    icon: Map,
    group: "Spatial",
  },
  mapsapi: {
    title: "Vector Tile API",
    description: "Vector map tiles for web and mobile applications.",
    icon: Globe,
    group: "Maps & Tiles",
    premium: "partial",
  },
  datasetsapi: {
    title: "Datasets API",
    description: "Programmatic access to datasets.",
    icon: Database,
    group: "Datasets",
  },
  ogcapis: {
    title: "OGC APIs",
    description: "Standards-based access to spatial features and tiles.",
    icon: Globe,
    group: "Spatial",
  },
};

const aliases: Record<string, string> = {
  predictiveav: "predictiveapi",
  esricompatiblelocator: "esricompatiblelocatorapi",
  esriapi: "esricompatiblelocatorapi",
  vectortileapi: "mapsapi",
  vectortilesapi: "mapsapi",
  datasetsinternal: "datasetsapi",
  ogcapi: "ogcapis",
};

const verifyOptions: (Omit<ProductPresentation, "group"> & { id: string })[] = [
  {
    id: "autocomplete",
    title: "Autocomplete",
    description: "Suggest addresses and localities as users type.",
    icon: Search,
  },
  {
    id: "address",
    title: "Address Verify",
    description: "Validate and geocode addresses.",
    icon: MapPin,
  },
  {
    id: "mailpoint",
    title: "MailPoint Verify",
    description: "Validate Australian postal delivery points.",
    icon: Mail,
    premium: "full",
  },
  {
    id: "phone",
    title: "Phone Verify",
    description: "Validate and normalise phone numbers.",
    icon: Phone,
    premium: "full",
  },
  {
    id: "email",
    title: "Email Verify",
    description: "Validate email addresses and check their domains.",
    icon: AtSign,
    premium: "full",
  },
];

const normaliseProductName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "");

const isVerifyProduct = (product: ApiProduct) =>
  [product.displayName, product.name].some((name) =>
    ["geoscapeverify", "geoscapeverifyapi", "verify", "verifyapi"].includes(
      normaliseProductName(name),
    ),
  );

// Verify already exists on app keys but is not in every public product catalogue.
const VERIFY_PRODUCT: ApiProduct = {
  name: "Geoscape Verify",
  displayName: "Geoscape Verify",
  premium: "full",
};

export function getApiProductOptions(
  products: ApiProduct[],
): ApiProductOption[] {
  const availableProducts = products.some(isVerifyProduct)
    ? products
    : [VERIFY_PRODUCT, ...products];
  return availableProducts.flatMap((product): ApiProductOption[] => {
    const names = [product.displayName, product.name].map(normaliseProductName);
    if (isVerifyProduct(product)) {
      // The five visible services share one product until Verify is split up.
      return verifyOptions.map((option) => ({
        ...option,
        id: `${product.name}-${option.id}`,
        product: {
          ...product,
          premium: product.premium ?? VERIFY_PRODUCT.premium,
        },
        group: "Geoscape Verify",
      }));
    }
    const presentation = names
      .map((name) => presentations[aliases[name] ?? name])
      .find(Boolean);
    return [
      {
        id: product.name,
        product: {
          ...product,
          premium: product.premium ?? presentation?.premium,
        },
        ...(presentation ?? {
          title: product.displayName,
          description: `Enable this app to access ${product.displayName}.`,
          icon: Database,
          group: "Other APIs",
        }),
        premium: presentation?.premium ?? product.premium,
      },
    ];
  });
}
